import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../../../components/layout';
import SEO from '../../../components/seo';
import { SettingProvider } from '../../../lib/context/settings';
import { AdsProvider } from '../../../lib/context/ads';
import { Col, Row, Collapse, Button } from 'antd';
import { Title } from '../../../components/styles';
import { device } from '../../../lib/theme';
import styled from 'styled-components';

const { Panel } = Collapse;

const HeroContainer = styled(Row)`
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 100px;
  margin: auto;
  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const BatteryFAQs: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="AAA Service Plans FAQ"
              description="Have a question about our AAA Carolinas Automotive Vehicle Extended Service Plans? Find your answers here."
              canonicalUrl="/automotive/battery/faq"
            />

            <HeroContainer align="middle" justify="center">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title style={{ margin: 'auto' }}>AAA Battery FAQ</Title>
              </Col>
            </HeroContainer>

            <Row style={{ paddingTop: 48 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Panel
                    header="What do I do if my battery has failed?"
                    key="1"
                  >
                    <p>
                      In the event your battery fails, your warranty will
                      provide you with a replacement. Replacement must originate
                      with requested battery service through AAA and not by a
                      third party.
                    </p>
                  </Panel>
                  <Panel
                    header="Does a battery do anything else other than start my vehicle?"
                    key="2"
                  >
                    <p>
                      The primary function of the battery is to provide power to
                      the starter and ignition system of the vehicle. In
                      addition, the battery supplies additional power to the
                      vehicle when load requirements exceed alternator output.
                    </p>
                  </Panel>
                  <Panel
                    header="What does the charge & retest battery test mean?"
                    key="3"
                  >
                    <p>
                      The battery is discharged and needs to be charged using a
                      dedicated battery charger for the appropriate amount of
                      time.
                    </p>
                  </Panel>
                  <Panel
                    header="What do I do next if I get the charge & retest result?"
                    key="4"
                  >
                    <p>
                      The battery needs to be charged using a dedicated charger.
                      Our AAA Car Care Centers are able to do this for you free
                      of charge.
                    </p>
                    <Button
                      type="primary"
                      href="http://locator.carolinas.aaa.com/?type=carcare"
                    >
                      Find a Location
                    </Button>
                  </Panel>
                  <Panel
                    header="Will my alternator charge my discharged battery?"
                    key="5"
                  >
                    <p>
                      The alternator in your vehicle is designed to maintain the
                      charge level of the battery. The alternator will not
                      charge a completely discharged battery.
                    </p>
                  </Panel>
                  <Panel
                    header="What are common causes of battery failure?"
                    key="6"
                  >
                    <ul>
                      <li>
                        Corrosion/terminal oxidation leads to charging and
                        discharging problems.
                      </li>
                      <li>
                        Temperature - heat can lead to premature failure or the
                        severe cold can strain your battery.
                      </li>
                    </ul>
                  </Panel>
                  <Panel header="How long does a battery last?" key="7">
                    <p>
                      An automotive battery in North America typically lasts 3-5
                      years.
                    </p>
                  </Panel>
                  <Panel
                    header="Does the AAA Battery come with a warranty?"
                    key="8"
                  >
                    <p>
                      Yes, we have an unmatched nationwide 3-year free
                      replacement warranty.
                    </p>
                  </Panel>
                  <Panel
                    header="What are the common warranty exclusions?"
                    key="9"
                  >
                    <p>
                      The top 3 things that void our battery warranty are:
                      <ul>
                        <ol>
                          1. not driving the vehicle over 6,000 miles annually
                        </ol>
                        <ol>2. not starting the vehicle for 90+ days and</ol>
                        <ol>
                          3. the vehicle’s charging and starting systems are not
                          working properly.
                        </ol>
                      </ul>
                    </p>
                  </Panel>
                  <Panel
                    header="What do I do if my vehicle does have to sit for an extended period of time (greater than 3 weeks)?"
                    key="10"
                  >
                    <p>
                      Use a battery tender to preserve the longevity of your
                      battery.
                    </p>
                  </Panel>
                  <Panel
                    header="What else can I do to avoid my battery from dying?"
                    key="11"
                  >
                    <p>
                      Have your battery checked at each service interval and
                      avoid long periods of time with the ignition on without
                      the engine running.
                    </p>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default BatteryFAQs;
